// store/slices/invoiceSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../axiosInstance";

// Initial state
const initialState = {
  invoices: [],
  invoice: null,
  userInvoices: [],
  loading: false,
  error: null,
  success: false,
  message: "",
};

// Create invoice
export const createInvoice = createAsyncThunk(
  "invoice/createInvoice",
  async (invoiceData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/invoice/create-invoice`, invoiceData);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create invoice",
      );
    }
  },
);

// Get all invoices
export const getAllInvoices = createAsyncThunk(
  "invoice/getAllInvoices",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/invoice/get-all-invoices`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch invoices",
      );
    }
  },
);

// Get single invoice by ID
export const getInvoiceById = createAsyncThunk(
  "invoice/getInvoiceById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/invoice/get-invoice/${id}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch invoice",
      );
    }
  },
);

// Get invoices by user ID
export const getUserInvoices = createAsyncThunk(
  "invoice/getUserInvoices",
  async (userId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/invoice/get-user-invoices/${userId}`);
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user invoices",
      );
    }
  },
);

// Update invoice
export const updateInvoice = createAsyncThunk(
  "invoice/updateInvoice",
  async ({ id, invoiceData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(
        `/invoice/update-invoice/${id}`,
        invoiceData,
      );
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update invoice",
      );
    }
  },
);

export const convertPointsToCash = createAsyncThunk(
  "user/convertPointsToCash",
  async ({ pointsToConvert }, { getState, rejectWithValue }) => {
    try {
      const { data } = await api.put(`/user/convert-points-to-cash`, {
        pointsToConvert,
      });
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to convert points",
      );
    }
  },
);

// Mark invoice as paid
export const markInvoicePaid = createAsyncThunk(
  "invoice/markInvoicePaid",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/invoice/mark-invoice-paid/${id}`, {});
      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to mark invoice as paid",
      );
    }
  },
);

// Delete invoice
export const deleteInvoice = createAsyncThunk(
  "invoice/deleteInvoice",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/invoice/delete-invoice/${id}`);
      return { ...data, id };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete invoice",
      );
    }
  },
);

// Invoice slice
const invoiceSlice = createSlice({
  name: "invoice",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
      state.message = "";
    },
    resetInvoiceState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Create invoice
      .addCase(createInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        state.invoices.unshift(action.payload.invoice);
      })
      .addCase(createInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get all invoices
      .addCase(getAllInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.invoices;
      })
      .addCase(getAllInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get single invoice
      .addCase(getInvoiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getInvoiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.invoice = action.payload.invoice;
      })
      .addCase(getInvoiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get user invoices
      .addCase(getUserInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload.invoices || action.payload;
      })
      .addCase(getUserInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update invoice
      .addCase(updateInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        state.invoice = action.payload.invoice;
        state.invoices = state.invoices.map((inv) =>
          inv._id === action.payload.invoice._id ? action.payload.invoice : inv,
        );
        state.userInvoices = state.userInvoices.map((inv) =>
          inv._id === action.payload.invoice._id ? action.payload.invoice : inv,
        );
      })
      .addCase(updateInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Mark invoice as paid
      .addCase(markInvoicePaid.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(markInvoicePaid.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        const updated = action.payload.invoice;
        state.invoice = updated;
        state.invoices = state.invoices.map((inv) =>
          inv._id === updated._id ? updated : inv,
        );
        state.userInvoices = state.userInvoices.map((inv) =>
          inv._id === updated._id ? updated : inv,
        );
      })
      .addCase(markInvoicePaid.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete invoice
      .addCase(deleteInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.message = action.payload.message;
        state.invoices = state.invoices.filter(
          (inv) => inv._id !== action.payload.id,
        );
        state.userInvoices = state.userInvoices.filter(
          (inv) => inv._id !== action.payload.id,
        );
        if (state.invoice?._id === action.payload.id) {
          state.invoice = null;
        }
      })
      .addCase(deleteInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearErrors, clearSuccess, resetInvoiceState } =
  invoiceSlice.actions;

export default invoiceSlice.reducer;
